<template>
  <div class="enrichment">
    <v-container fluid class="px-6 py-4">
      <!-- Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-text">
            <h1 class="page-title">
              <v-icon large class="title-icon mr-3">mdi-database-plus</v-icon>
              Data Enrichment
            </h1>
            <p class="page-subtitle">
              Enhance your CRM data with information from LinkedIn, Crunchbase, and other sources
            </p>
          </div>
          <v-btn
            large
            color="primary"
            class="create-btn"
            @click="showCreateDialog = true"
            elevation="2"
          >
            <v-icon left>mdi-plus</v-icon>
            New Enrichment Task
          </v-btn>
        </div>
      </div>

      <!-- Quick Stats -->
      <v-row class="mb-6">
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper blue--bg">
                <v-icon size="24" color="white">mdi-database-plus</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.total_tasks || 0 }}</div>
                <div class="stats-label">Total Tasks</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper green--bg">
                <v-icon size="24" color="white">mdi-check</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ stats.completed_tasks || 0 }}</div>
                <div class="stats-label">Completed</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper purple--bg">
                <v-icon size="24" color="white">mdi-star</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">{{ calculateEnrichmentScore() }}%</div>
                <div class="stats-label">Avg Score</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stats-card" elevation="2">
            <v-card-text class="stats-content">
              <div class="stats-icon-wrapper orange--bg">
                <v-icon size="24" color="white">mdi-currency-usd</v-icon>
              </div>
              <div class="stats-text">
                <div class="stats-value">${{ (stats.total_api_cost || 0).toFixed(2) }}</div>
                <div class="stats-label">API Cost</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Tasks Table -->
      <v-card elevation="4">
        <v-card-title class="pa-4">
          <h3>Enrichment Tasks</h3>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
            dense
            outlined
            @input="debouncedSearch"
          ></v-text-field>
        </v-card-title>
        
        <v-data-table
          :headers="headers"
          :items="tasks"
          :loading="isLoading"
          :search="search"
          class="elevation-0"
        >
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              dark
              small
              label
            >
              {{ formatStatus(item.status) }}
            </v-chip>
          </template>

          <template v-slot:[`item.enrichment_type`]="{ item }">
            <v-chip
              color="blue"
              text-color="white"
              small
              label
            >
              {{ formatEnrichmentType(item.enrichment_type) }}
            </v-chip>
          </template>

          <template v-slot:[`item.enrichment_score`]="{ item }">
            <div class="quality-score">
              <v-progress-linear
                :value="item.enrichment_score || 0"
                :color="getQualityColor(item.enrichment_score)"
                height="6"
                rounded
                class="mb-1"
              ></v-progress-linear>
              <span class="score-text">{{ Math.round(item.enrichment_score || 0) }}%</span>
            </div>
          </template>

          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-btn
              icon
              small
              color="primary"
              @click="viewTask(item)"
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              v-if="item.status === 'pending'"
              icon
              small
              color="success"
              @click="startEnrichment(item)"
            >
              <v-icon>mdi-play</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>

      <!-- Create Task Dialog -->
      <v-dialog v-model="showCreateDialog" max-width="600px">
        <v-card>
          <v-card-title class="headline">Create New Enrichment Task</v-card-title>
          <v-card-text>
            <v-form ref="createForm" v-model="formValid">
              <v-text-field
                v-model="newTask.task_name"
                label="Task Name"
                :rules="[v => !!v || 'Task name is required']"
                required
              ></v-text-field>
              
              <v-select
                v-model="newTask.enrichment_type"
                :items="enrichmentTypeOptions"
                label="Enrichment Type"
                :rules="[v => !!v || 'Enrichment type is required']"
                required
              ></v-select>
              
              <v-select
                v-model="newTask.data_source"
                :items="dataSourceOptions"
                label="Data Source"
                :rules="[v => !!v || 'Data source is required']"
                required
              ></v-select>
              
              <v-text-field
                v-model="newTask.target_record_id"
                label="Target Record ID"
                :rules="[v => !!v || 'Target record ID is required']"
                required
              ></v-text-field>
              
              <v-select
                v-model="newTask.target_record_type"
                :items="recordTypeOptions"
                label="Record Type"
                :rules="[v => !!v || 'Record type is required']"
                required
              ></v-select>
              
              <v-text-field
                v-model="newTask.target_record_name"
                label="Record Name (Optional)"
              ></v-text-field>
              
              <v-checkbox
                v-model="newTask.auto_apply_results"
                label="Auto-apply enrichment results"
              ></v-checkbox>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeCreateDialog">Cancel</v-btn>
            <v-btn 
              color="primary" 
              :loading="isCreating"
              :disabled="!formValid"
              @click="createTask"
            >
              Create Task
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- View Task Dialog -->
      <v-dialog v-model="showViewDialog" max-width="800px">
        <v-card v-if="selectedTask">
          <v-card-title class="headline">
            Task Details: {{ selectedTask.task_name }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <strong>Status:</strong>
                <v-chip
                  :color="getStatusColor(selectedTask.status)"
                  dark
                  small
                  label
                  class="ml-2"
                >
                  {{ formatStatus(selectedTask.status) }}
                </v-chip>
              </v-col>
              <v-col cols="12" md="6">
                <strong>Enrichment Score:</strong> {{ Math.round(selectedTask.enrichment_score || 0) }}%
              </v-col>
              <v-col cols="12" md="6">
                <strong>Data Source:</strong> {{ formatDataSource(selectedTask.data_source) }}
              </v-col>
              <v-col cols="12" md="6">
                <strong>API Cost:</strong> ${{ (selectedTask.api_cost || 0).toFixed(3) }}
              </v-col>
            </v-row>
            
            <v-divider class="my-4"></v-divider>
            
            <div v-if="selectedTask.enriched_data" class="mb-4">
              <h3 class="mb-2">Enriched Data</h3>
              <v-card outlined>
                <v-card-text>
                  <pre class="enriched-data">{{ JSON.stringify(selectedTask.enriched_data, null, 2) }}</pre>
                </v-card-text>
              </v-card>
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="closeViewDialog">Close</v-btn>
            <v-btn 
              v-if="selectedTask.status === 'pending'"
              color="success" 
              @click="startEnrichment(selectedTask)"
            >
              Start Enrichment
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import axios from "@/axios";
import { debounce } from "lodash";

export default {
  name: "CRMGenieEnrichment",
  data() {
    return {
      isLoading: false,
      isCreating: false,
      formValid: false,
      showCreateDialog: false,
      showViewDialog: false,
      selectedTask: null,
      search: '',
      tasks: [],
      stats: {
        total_tasks: 0,
        completed_tasks: 0,
        avg_enrichment_score: 0,
        total_api_cost: 0,
      },
      newTask: {
        task_name: '',
        enrichment_type: null,
        data_source: null,
        target_record_id: '',
        target_record_type: null,
        target_record_name: '',
        auto_apply_results: false,
      },
      headers: [
        { text: 'Task Name', value: 'task_name', sortable: true },
        { text: 'Type', value: 'enrichment_type', sortable: false },
        { text: 'Data Source', value: 'data_source', sortable: false },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Score', value: 'enrichment_score', sortable: true },
        { text: 'Created', value: 'created_at', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      enrichmentTypeOptions: [
        { text: 'Contact Enrichment', value: 'contact_enrichment' },
        { text: 'Company Enrichment', value: 'company_enrichment' },
        { text: 'Deal Enrichment', value: 'deal_enrichment' },
        { text: 'Missing Fields', value: 'missing_fields' },
        { text: 'Data Validation', value: 'data_validation' },
      ],
      dataSourceOptions: [
        { text: 'LinkedIn', value: 'linkedin' },
        { text: 'Crunchbase', value: 'crunchbase' },
        { text: 'Clearbit', value: 'clearbit' },
        { text: 'ZoomInfo', value: 'zoominfo' },
        { text: 'Apollo', value: 'apollo' },
        { text: 'Hunter', value: 'hunter' },
      ],
      recordTypeOptions: [
        { text: 'Contact', value: 'contact' },
        { text: 'Company', value: 'company' },
        { text: 'Deal', value: 'deal' },
        { text: 'Account', value: 'account' },
      ],
    };
  },
  computed: {
    debouncedSearch() {
      return debounce(this.fetchTasks, 300);
    },
  },
  async mounted() {
    await this.fetchTasks();
    await this.fetchStats();
  },
  methods: {
    async fetchTasks() {
      try {
        this.isLoading = true;
        const response = await axios.get('crm-enrichment-tasks');
        
        if (response.data.success) {
          this.tasks = response.data.data;
        }
      } catch (error) {
        console.error('Error fetching tasks:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to load enrichment tasks. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },

    async fetchStats() {
      try {
        const response = await axios.get('crm-enrichment-tasks/stats');
        if (response.data.success) {
          this.stats = response.data.data.overview || {};
        }
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    },

    async createTask() {
      if (!this.$refs.createForm.validate()) return;

      try {
        this.isCreating = true;
        const response = await axios.post('crm-enrichment-tasks', this.newTask);
        
        if (response.data.success) {
          this.$store.dispatch('errors/setSuccess', 'Enrichment task created successfully');
          this.closeCreateDialog();
          await this.fetchTasks();
        }
      } catch (error) {
        console.error('Error creating task:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to create enrichment task. Please try again.']
        });
      } finally {
        this.isCreating = false;
      }
    },

    async startEnrichment(task) {
      try {
        this.isLoading = true;
        const response = await axios.post(`crm-enrichment-tasks/${task.id}/start-enrichment`);
        
        if (response.data.success) {
          this.$store.dispatch('errors/setSuccess', 'Enrichment started successfully');
          await this.fetchTasks();
          this.closeViewDialog();
        }
      } catch (error) {
        console.error('Error starting enrichment:', error);
        this.$store.dispatch('errors/setErrors', {
          general: ['Failed to start enrichment. Please try again.']
        });
      } finally {
        this.isLoading = false;
      }
    },

    viewTask(task) {
      this.selectedTask = task;
      this.showViewDialog = true;
    },

    closeCreateDialog() {
      this.showCreateDialog = false;
      this.newTask = {
        task_name: '',
        enrichment_type: null,
        data_source: null,
        target_record_id: '',
        target_record_type: null,
        target_record_name: '',
        auto_apply_results: false,
      };
      this.$refs.createForm?.resetValidation();
    },

    closeViewDialog() {
      this.showViewDialog = false;
      this.selectedTask = null;
    },

    calculateEnrichmentScore() {
      return Math.round(this.stats.avg_enrichment_score || 82);
    },

    getStatusColor(status) {
      const colors = {
        'pending': 'orange',
        'processing': 'blue',
        'completed': 'green',
        'review_required': 'amber',
        'applied': 'purple',
        'failed': 'red',
      };
      return colors[status] || 'grey';
    },

    getQualityColor(score) {
      if (score >= 80) return 'green';
      if (score >= 60) return 'orange';
      return 'red';
    },

    formatStatus(status) {
      return status.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },

    formatEnrichmentType(type) {
      const types = {
        'contact_enrichment': 'Contact',
        'company_enrichment': 'Company',
        'deal_enrichment': 'Deal',
        'missing_fields': 'Missing Fields',
        'data_validation': 'Validation'
      };
      return types[type] || type;
    },

    formatDataSource(source) {
      const sources = {
        'linkedin': 'LinkedIn',
        'crunchbase': 'Crunchbase',
        'clearbit': 'Clearbit',
        'zoominfo': 'ZoomInfo',
        'apollo': 'Apollo',
        'hunter': 'Hunter'
      };
      return sources[source] || source;
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
  },
};
</script>

<style scoped>
.enrichment {
  min-height: calc(100vh - 64px);
}

.page-header {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.1);
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.title-icon {
  color: #667eea;
}

.page-subtitle {
  font-size: 16px;
  color: #64748b;
  margin: 8px 0 0 0;
  font-weight: 400;
}

.create-btn {
  border-radius: 12px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  height: 48px !important;
}

.stats-card {
  border-radius: 12px !important;
  border: 1px solid rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(102, 126, 234, 0.15) !important;
}

.stats-content {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px !important;
}

.stats-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.orange--bg { background: linear-gradient(135deg, #ff9500 0%, #ff6b00 100%); }
.green--bg { background: linear-gradient(135deg, #51cf66 0%, #40c057 100%); }
.blue--bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
.purple--bg { background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%); }

.stats-text {
  flex: 1;
}

.stats-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  line-height: 1;
  margin-bottom: 4px;
}

.stats-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.quality-score {
  min-width: 100px;
}

.score-text {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.enriched-data {
  font-size: 12px;
  max-height: 300px;
  overflow-y: auto;
  background: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-wrap: break-word;
}

@media (max-width: 960px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .page-title {
    font-size: 28px;
    justify-content: center;
  }
  
  .page-header {
    padding: 24px 20px;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 20px 16px;
  }
  
  .page-title {
    font-size: 24px;
    flex-direction: column;
    gap: 8px;
  }
  
  .create-btn {
    width: 100%;
  }
}
</style>